/**
 * Analytics Service
 * API service for Dashboard Analytics and Statistics
 */

import { api } from '../utils/api';
import type {
  DashboardAnalyticsResponse,
  DashboardFilters,
  ChatStatistics,
  PopularSubject,
  FrequentQuestion,
} from '../types/analytics';

/**
 * Analytics API Service
 * Handles all analytics and dashboard statistics API calls
 */
export const analyticsService = {
  /**
   * Get complete dashboard analytics
   * 
   * @param filters - Time period and date range filters
   * @returns Dashboard analytics data
   */
  getDashboardAnalytics: async (
    filters?: DashboardFilters
  ): Promise<DashboardAnalyticsResponse> => {
    const params = new URLSearchParams();
    
    if (filters?.period) {
      params.append('period', filters.period);
    }
    
    if (filters?.date_from) {
      params.append('date_from', filters.date_from);
    }
    
    if (filters?.date_to) {
      params.append('date_to', filters.date_to);
    }
    
    const queryString = params.toString();
    return api.get<DashboardAnalyticsResponse>(
      `/dashboard/analytics/dashboard/${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get chat statistics
   * 
   * @param filters - Date range filters
   * @returns Chat statistics
   */
  getChatStatistics: async (
    filters?: Pick<DashboardFilters, 'date_from' | 'date_to'>
  ): Promise<{
    statistics: ChatStatistics;
    popular_subjects: PopularSubject[];
    trends: any[];
  }> => {
    const params = new URLSearchParams();
    
    if (filters?.date_from) {
      params.append('date_from', filters.date_from);
    }
    
    if (filters?.date_to) {
      params.append('date_to', filters.date_to);
    }
    
    const queryString = params.toString();
    return api.get(
      `/dashboard/analytics/chat/statistics/${queryString ? `?${queryString}` : ''}`
    );
  },

  /**
   * Get popular chat subjects
   * 
   * @param limit - Number of subjects to return
   * @param filters - Date range filters
   * @returns Popular subjects
   */
  getPopularSubjects: async (
    limit = 10,
    filters?: Pick<DashboardFilters, 'date_from' | 'date_to'>
  ): Promise<{ subjects: PopularSubject[]; total_count: number }> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    
    if (filters?.date_from) {
      params.append('date_from', filters.date_from);
    }
    
    if (filters?.date_to) {
      params.append('date_to', filters.date_to);
    }
    
    return api.get(
      `/dashboard/analytics/chat/subjects/?${params.toString()}`
    );
  },

  /**
   * Get frequently asked questions
   * 
   * @param limit - Number of questions to return
   * @param minCount - Minimum occurrence count
   * @param approvedOnly - Only return approved questions
   * @returns Frequent questions
   */
  getFrequentQuestions: async (
    limit = 20,
    minCount = 2,
    approvedOnly = true
  ): Promise<{ questions: FrequentQuestion[]; total_count: number }> => {
    const params = new URLSearchParams();
    params.append('limit', limit.toString());
    params.append('min_count', minCount.toString());
    params.append('approved_only', approvedOnly.toString());
    
    return api.get(
      `/dashboard/analytics/chat/faq/?${params.toString()}`
    );
  },

  /**
   * Export dashboard report to Excel
   * 
   * @param filters - Time period and date range filters
   * @param language - Report language (fa or en)
   * @returns Downloads Excel file
   */
  exportDashboardReport: async (
    filters?: DashboardFilters,
    language: string = 'fa'
  ): Promise<void> => {
    const params = new URLSearchParams();
    
    if (filters?.period) {
      params.append('period', filters.period);
    }
    
    if (filters?.date_from) {
      params.append('date_from', filters.date_from);
    }
    
    if (filters?.date_to) {
      params.append('date_to', filters.date_to);
    }
    
    params.append('language', language);
    
    const queryString = params.toString();
    const url = `/dashboard/analytics/dashboard/export/${queryString ? `?${queryString}` : ''}`;
    
    // Create a temporary link and trigger download
    const response = await fetch(`${import.meta.env.VITE_API_BASE_URL}${url}`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token') || ''}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to export report');
    }
    
    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    
    // Extract filename from Content-Disposition header or use default
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'dashboard_report.xlsx';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  },
};
